import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, ArrowUp, X, FileText, Camera, ImageIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export interface ChatAttachment {
  id: string;
  file: File;
  previewUrl?: string;
  type: "image" | "file";
}

interface ChatInputProps {
  onSend: (message: string, attachments?: ChatAttachment[]) => void;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function useTypewriter(texts: string[], typingSpeed = 50, deletingSpeed = 30, pauseMs = 2000) {
  const [displayed, setDisplayed] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (texts.length === 0) return;
    const current = texts[textIndex % texts.length];

    if (!isDeleting && charIndex <= current.length) {
      if (charIndex === current.length) {
        const timeout = setTimeout(() => setIsDeleting(true), pauseMs);
        return () => clearTimeout(timeout);
      }
      const timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, typingSpeed);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && charIndex >= 0) {
      if (charIndex === 0) {
        setIsDeleting(false);
        setTextIndex((i) => (i + 1) % texts.length);
        setDisplayed("");
        return;
      }
      const timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIndex - 1));
        setCharIndex((c) => c - 1);
      }, deletingSpeed);
      return () => clearTimeout(timeout);
    }
  }, [texts, textIndex, charIndex, isDeleting, typingSpeed, deletingSpeed, pauseMs]);

  return displayed;
}

export function ChatInput({
  onSend,
  placeholder,
  disabled = false,
  value: externalValue,
}: ChatInputProps) {
  const { t } = useI18n();
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const desktopPlaceholders = [
    t("chat.placeholder1"),
    t("chat.placeholder2"),
    t("chat.placeholder3"),
    t("chat.placeholder4"),
    t("chat.placeholder5"),
  ];

  const mobilePlaceholders = [
    t("chat.placeholderMobile1"),
    t("chat.placeholderMobile2"),
    t("chat.placeholderMobile3"),
    t("chat.placeholderMobile4"),
    t("chat.placeholderMobile5"),
  ];

  const activePlaceholders = placeholder ? [placeholder] : (isMobile ? mobilePlaceholders : desktopPlaceholders);
  const typewriterText = useTypewriter(
    activePlaceholders.length > 1 ? activePlaceholders : [],
    45,
    25,
    2200
  );
  const displayPlaceholder = activePlaceholders.length > 1 ? typewriterText : activePlaceholders[0];

  useEffect(() => {
    if (externalValue !== undefined) setValue(externalValue);
  }, [externalValue]);

  // Autofocus on desktop only
  useEffect(() => {
    if (window.innerWidth >= 768 && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [value]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const newAttachments: ChatAttachment[] = [];
    Array.from(files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t("chat.fileTooLarge").replace("{name}", file.name));
        return;
      }
      const isImage = file.type.startsWith("image/");
      newAttachments.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        type: isImage ? "image" : "file",
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      });
    });
    setAttachments((prev) => [...prev, ...newAttachments]);
  }, [t]);

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSubmit = () => {
    if ((!value.trim() && attachments.length === 0) || disabled) return;
    onSend(value.trim(), attachments.length > 0 ? attachments : undefined);
    setValue("");
    setAttachments([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSend = value.trim() || attachments.length > 0;
  const isTablet = !isMobile && typeof window !== "undefined" && window.innerWidth < 1024;
  const showCamera = isMobile || isTablet;

  return (
    <div className="w-full max-w-[680px] mx-auto px-4">
      <div className="bg-input-bg rounded-2xl border border-border shadow-sm hover:shadow-md transition-shadow">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pt-3">
            {attachments.map((att) => (
              <div key={att.id} className="relative group">
                {att.type === "image" && att.previewUrl ? (
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                    <img src={att.previewUrl} alt={att.file.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-secondary rounded-lg px-3 py-2 text-xs text-secondary-foreground max-w-[180px]">
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{att.file.name}</span>
                  </div>
                )}
                <button
                  onClick={() => removeAttachment(att.id)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-foreground text-background rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative flex items-center">
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex-shrink-0 p-3 pl-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>

            {menuOpen && (
              <div className="absolute bottom-full left-2 mb-2 bg-popover border border-border rounded-xl shadow-lg py-1 min-w-[180px] z-50">
                {showCamera && (
                  <button
                    onClick={() => { setMenuOpen(false); cameraInputRef.current?.click(); }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-popover-foreground hover:bg-accent transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                    {t("chat.takePhoto")}
                  </button>
                )}
                <button
                  onClick={() => { setMenuOpen(false); imageInputRef.current?.click(); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-popover-foreground hover:bg-accent transition-colors"
                >
                  <ImageIcon className="w-4 h-4" />
                  {t("chat.addPhotos")}
                </button>
                <button
                  onClick={() => { setMenuOpen(false); fileInputRef.current?.click(); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-popover-foreground hover:bg-accent transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  {t("chat.uploadFiles")}
                </button>
              </div>
            )}
          </div>

          <input ref={imageInputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.json,.xml" className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

          <div className="flex-1 relative flex items-center">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder=" "
              disabled={disabled}
              rows={1}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
              enterKeyHint={isMobile ? "send" : undefined}
              inputMode="text"
              className="w-full bg-transparent py-3.5 px-1 text-base sm:text-sm text-foreground resize-none outline-none max-h-[200px]"
            />
            {!value && (
              <span className="absolute left-1 top-1/2 -translate-y-1/2 text-base sm:text-sm text-muted-foreground pointer-events-none whitespace-nowrap overflow-hidden">
                {displayPlaceholder}
              </span>
            )}
          </div>

          <div className="flex items-center pr-2">
            <button
              onClick={handleSubmit}
              disabled={!canSend || disabled}
              className="p-2 bg-primary text-primary-foreground rounded-full disabled:opacity-30 hover:opacity-80 transition-opacity"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
